/**
 * ONE CLIENT — its engagements, and the work opened against them.
 *
 * An engagement is the period a piece of work belongs to; it is created here
 * because that is where a person thinks about a client's year. The work below
 * it is the same work the board shows, and every word about its state — the
 * label and the tone — is the projection's, read rather than re-decided.
 *
 * ONE RESIDUAL, stated rather than worked around.
 *
 * There is no read of a single client's work. The board is read whole and the
 * rows for this client are selected out of it by id. That is a selection, not
 * a judgment — no status, label, tone or grouping is recomputed — but it does
 * mean this screen carries the whole firm's board to show one client's part.
 *
 * Every date on this screen arrives already read for a person —
 * `Client.createdAtText`, `Engagement.periodText`, `WorkSummary.periodText`.
 * The machine values travel too and sit in a title, so a locator is one hover
 * away; none of them is ever formatted here.
 */
import { useCallback, useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ClientResponse, EngagementListResponse, EngagementResponse, WorkBoardResponse,
  type Client, type Engagement, type WorkSummary,
} from "@leveri/contracts";
import {
  Button, FileTitle, Notice, Reading, RuledList, RuledRow, Section, SectionHead, Select, StateText, TextField,
  markerTone,
} from "../file/index.js";
import { call, failureMessage } from "../api.js";
import { Desk, usePlaceWords } from "./shell.js";
import { ChatBox } from "./chat.js";
import { useVocabularies, word } from "./vocabularies.js";
import { ConnectionsSection } from "./connections.js";

export function ClientScreen() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const place = usePlaceWords();
  // Every closed vocabulary this screen renders — the kinds of engagement and
  // the two status words — is served, so the browser holds none of them.
  const { terms } = useVocabularies();
  const [client, setClient] = useState<Client | null>(null);
  const [engagements, setEngagements] = useState<Engagement[] | null>(null);
  const [work, setWork] = useState<WorkSummary[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [addFailure, setAddFailure] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    setFailure(null);
    try {
      const [one, list, board] = await Promise.all([
        call("GET", `/api/clients/${clientId}`, ClientResponse),
        call("GET", `/api/clients/${clientId}/engagements`, EngagementListResponse),
        call("GET", "/api/work", WorkBoardResponse),
      ]);
      setClient(one.client);
      setEngagements(list.engagements);
      setWork(board.groups.flatMap((group) => group.work).filter((row) => row.clientId === clientId));
    } catch (error) {
      setFailure(failureMessage(error));
    }
  }, [clientId]);

  useEffect(() => { void load(); }, [load]);

  async function add(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (adding || !clientId) return;
    const form = event.currentTarget;
    const values = new FormData(form);
    const read = (name: string) => String(values.get(name) ?? "");
    setAdding(true);
    setAddFailure(null);
    try {
      await call("POST", `/api/clients/${clientId}/engagements`, EngagementResponse, {
        title: read("title"),
        engagementType: read("engagementType"),
        periodStart: read("periodStart"),
        cutOff: read("cutOff"),
        currency: read("currency"),
      });
      form.reset();
      await load();
    } catch (error) {
      setAddFailure(failureMessage(error));
    } finally {
      setAdding(false);
    }
  }

  if (failure) {
    return (
      <Desk current="clients" measure="wide">
        <Notice>{failure}</Notice>
        <button type="button" onClick={() => void load()} className="mt-3 text-[13px] text-stone hover:text-ink">
          Try again
        </button>
      </Desk>
    );
  }
  if (!client) {
    return (
      <Desk current="clients" measure="wide">
        <Reading>Opening the client…</Reading>
      </Desk>
    );
  }

  return (
    <Desk current="clients" measure="wide">
      <FileTitle
        kicker="Client"
        size={28}
        meta={`${word(terms?.clientStatuses, client.status)} · opened ${client.createdAtText}`}
      >
        {client.name}
      </FileTitle>

      {/* The chat box, scoped to this client: never asks who it is for. */}
      <div className="mt-6 max-w-[820px]"><ChatBox scope={{ clientId: client.id }} placeholder={`What should Leveri do for ${client.name}?`} /></div>

      <Section>
        <SectionHead aside={engagements ? String(engagements.length) : null}>{place.Engagements}</SectionHead>
        {!engagements && <Reading>Loading the {place.engagements}…</Reading>}
        {engagements && engagements.length === 0 && (
          <p className="text-sm text-stone">No {place.engagements} yet. Add the period {place.client}&rsquo;s work belongs to below.</p>
        )}
        {engagements && engagements.length > 0 && (
          <RuledList>
            {/* R5.1: an engagement is a place you can open, not a line you
                can only read. The row is the way in. */}
            {engagements.map((engagement) => (
              <RuledRow
                as="button"
                key={engagement.id}
                className="px-3"
                onClick={() => navigate(`/engagements/${engagement.id}`)}
              >
                <span className="truncate text-sm font-medium text-ink">{engagement.title}</span>
                <span
                  className="text-[13px] text-stone [font-variant-numeric:tabular-nums]"
                  title={`${engagement.periodStart} – ${engagement.cutOff}`}
                >
                  {word(terms?.engagementTypes, engagement.engagementType)} · {engagement.periodText}
                  {" · "}{word(terms?.engagementStatuses, engagement.status)}
                </span>
              </RuledRow>
            ))}
          </RuledList>
        )}
      </Section>

      <Section>
        <SectionHead>{place.engagement === "period" ? "Add a period" : "Add an engagement"}</SectionHead>
        <form onSubmit={add} className="grid grid-cols-1 items-end gap-4 md:grid-cols-2">
          <TextField name="title" label="Title" required disabled={adding} />
          {/* The kinds of engagement, with their words, as the server sends
              them. Disabled until they arrive: a choice offered before its
              words exist would ask a person to pick between raw enums. */}
          <Select name="engagementType" label="Type" required disabled={adding || !terms} defaultValue="">
            <option value="" disabled>{terms ? "Choose" : "Loading…"}</option>
            {(terms?.engagementTypes ?? []).map((kind) => (
              <option key={kind.value} value={kind.value}>{kind.label}</option>
            ))}
          </Select>
          <TextField name="periodStart" type="date" label="Period start" required disabled={adding} />
          <TextField name="cutOff" type="date" label="Cut-off" required disabled={adding} />
          {/* R8.1 — every figure of the engagement's work is denominated in
              it, so it is asked for, like the period. No default: the product
              assumes no country's currency for anybody. */}
          <TextField name="currency" label="Currency" hint="Three-letter code, such as USD, GBP or EUR" required maxLength={3} disabled={adding} />
          <div className="md:col-span-2">
            <Button type="submit" disabled={adding}>Add the {place.engagement}</Button>
          </div>
        </form>
        {adding && <div className="mt-2"><Reading>Adding the {place.engagement}…</Reading></div>}
        {addFailure && <div className="mt-2"><Notice>{addFailure}</Notice></div>}
      </Section>

      <Section>
        <SectionHead aside={work ? String(work.length) : null}>Work for this client</SectionHead>
        {!work && <Reading>Loading the work…</Reading>}
        {work && work.length === 0 && (
          <p className="text-sm text-stone">No work has been opened for this client yet.</p>
        )}
        {work && work.length > 0 && (
          <RuledList>
            {work.map((row) => (
              <RuledRow as="button" key={row.id} className="px-3" onClick={() => navigate(`/work/${row.id}`)}>
                <div className="grid grid-cols-1 items-baseline gap-x-6 gap-y-1 md:grid-cols-[minmax(0,1fr)_auto]">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-ink">{row.title}</div>
                    <div
                      className="mt-0.5 text-[13px] text-stone [font-variant-numeric:tabular-nums]"
                      title={`${row.periodStart} – ${row.cutOff}`}
                    >
                      {row.engagementTitle} · {row.periodText} · {row.currency}
                    </div>
                  </div>
                  <StateText tone={markerTone(row.tone)} className="text-[13px]">{row.statusLabel}</StateText>
                </div>
              </RuledRow>
            ))}
          </RuledList>
        )}
      </Section>

      {/* ADR-0024 T3: the client's books in another system. Shown only where the deployment can connect one. */}
      <ConnectionsSection clientId={client.id} clientName={client.name} />
    </Desk>
  );
}
